import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ArrowLeft, Building2, ExternalLink, Home, User } from 'lucide-react'
import { isFieldAppHost, normalAppUrlForPath } from '../../lib/fieldAppHost'
import { SmeFeedbackOverlay } from '../feedback/SmeFeedbackOverlay'

type FieldAppShellProps = {
  title: string
  eyebrow: string
  subtitle?: string
  progress?: number
  progressLabel?: string
  backTo?: string
  backLabel?: string
  showBackControl?: boolean
  /**
   * Intercept the header back arrow with custom logic. When provided, the
   * arrow renders as a <button> that calls this handler instead of
   * navigating to `backTo`. Pages with an internal wizard step this is
   * used to step backward one checkpoint before bailing to the parent
   * route.
   */
  onBack?: () => void
  rightSlot?: ReactNode
  children: ReactNode
  contentClassName?: string
  showNormalAppReturn?: boolean
}

export function FieldAppShell({
  title,
  eyebrow,
  subtitle,
  progress,
  progressLabel,
  backTo = '..',
  backLabel = 'Back',
  showBackControl = true,
  onBack,
  rightSlot,
  children,
  contentClassName = 'mx-auto w-full max-w-lg space-y-4 px-4 py-4 pb-24',
  showNormalAppReturn = false,
}: FieldAppShellProps) {
  const boundedProgress = typeof progress === 'number'
    ? Math.max(0, Math.min(100, progress))
    : null
  const isGovernedAppHost = isFieldAppHost()
  const normalAppHref = normalAppUrlForPath('/dashboard')
  const backHref = showBackControl && isGovernedAppHost && showNormalAppReturn ? normalAppHref : null

  const backBtnClass = 'flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg text-white transition-colors hover:bg-white/15'
  const backBtnStyle = { background: 'rgba(255,255,255,0.1)' }

  return (
    <div className="min-h-screen bg-[#f4f8fd] text-slate-900">
      <header className="sticky top-0 z-30 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)', background: 'var(--brand-deep)' }}>
        <div className="mx-auto flex w-full max-w-lg items-center gap-2 px-4 pb-3 pt-2">
          {!showBackControl ? (
            <div className="h-[30px] w-0 shrink-0" aria-hidden="true" />
          ) : onBack ? (
            <button type="button" onClick={onBack} aria-label={backLabel} className={backBtnClass} style={backBtnStyle}>
              <ArrowLeft size={16} />
            </button>
          ) : backHref ? (
            <a href={backHref} target="_blank" rel="noreferrer" aria-label={backLabel} className={backBtnClass} style={backBtnStyle}>
              <ArrowLeft size={16} />
            </a>
          ) : (
            <Link to={backTo} aria-label={backLabel} className={backBtnClass} style={backBtnStyle}>
              <ArrowLeft size={16} />
            </Link>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-[9px] font-extrabold uppercase tracking-[0.16em] text-white/55">
              {eyebrow}
            </p>
            <h1 className="mt-0.5 truncate font-brand text-[15px] font-bold leading-tight text-white">{title}</h1>
            {subtitle && <p className="truncate text-[11px] font-semibold text-white/55">{subtitle}</p>}
          </div>
          {rightSlot ? (
            <div className="shrink-0">{rightSlot}</div>
          ) : (
            <span
              className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-[3px] text-[9px] font-extrabold uppercase tracking-[0.08em]"
              style={{ background: 'rgba(15,139,127,0.25)', color: '#7dfff5' }}
            >
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: '#7dfff5', animation: 'fs-sync-pulse 2s infinite' }}
              />
              Synced
            </span>
          )}
        </div>
        {boundedProgress !== null && (
          <div className="mx-auto w-full max-w-lg px-4 pb-3">
            <div className="mb-1 flex items-center justify-between text-xs font-semibold text-slate-500">
              <span>{progressLabel ?? 'Progress'}</span>
              <span>{boundedProgress}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full rounded-full bg-[var(--brand-primary)] transition-all duration-300"
                style={{ width: `${boundedProgress}%` }}
              />
            </div>
          </div>
        )}
      </header>

      <main className={contentClassName}>{children}</main>
      <SmeFeedbackOverlay />
      <FieldAppBottomNav />
    </div>
  )
}

const NAV_ITEMS = [
  { to: '/today', label: 'Today', icon: Home, match: (p: string) => p === '/today' },
  { to: '/facilities', label: 'Facilities', icon: Building2, match: (p: string) => p.startsWith('/facilities') },
  { to: '/profile', label: 'Profile', icon: User, match: (p: string) => p.startsWith('/profile') },
] as const

function FieldAppBottomNav() {
  const { pathname } = useLocation()
  const normalAppHref = normalAppUrlForPath('/dashboard')
  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-[80] border-t bg-white px-1.5 pb-[calc(0.5rem+env(safe-area-inset-bottom))]"
      style={{ borderColor: 'rgba(23,86,141,0.16)' }}
    >
      <div className="mx-auto flex max-w-lg items-center justify-around py-1.5">
        {NAV_ITEMS.map((item) => {
          const active = item.match(pathname)
          const Icon = item.icon
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`flex flex-col items-center gap-0.5 rounded-lg px-2.5 py-1.5 text-[10px] font-bold transition-colors ${
                active
                  ? 'bg-[var(--brand-light)] text-[var(--brand-primary)]'
                  : 'text-slate-400 hover:bg-slate-50'
              }`}
            >
              <Icon size={18} />
              {item.label}
            </Link>
          )
        })}
        <a
          href={normalAppHref}
          className="flex flex-col items-center gap-0.5 rounded-lg px-2.5 py-1.5 text-[10px] font-bold text-slate-400 transition-colors hover:bg-slate-50"
        >
          <ExternalLink size={18} />
          Exit
        </a>
      </div>
    </nav>
  )
}
